import keys from 'constants/keys'
import dayjs from 'dayjs'
import { Content, Subtask, TaskAudience, TaskFormData, StepType } from './types'
import { PRIORITY_VALUES } from '../utils'
import { TaskPdfData, TaskPdfContent, TaskPdfSubtask } from '../PDF/types'
import { separateTaskContents, resolveAllowedOverdueDays } from './taskSubmission'

export type OrderedTaskPdfItem = {
  type: 'info_box' | 'subtask'
  data: TaskPdfContent | TaskPdfSubtask
}

/** Rebuild the combined editor order without collapsing consecutive or trailing Info boxes. */
export function orderTaskPdfItems(subtasks: TaskPdfSubtask[], contents: TaskPdfContent[]): OrderedTaskPdfItem[] {
  const orderedItems: OrderedTaskPdfItem[] = []
  const orderedSubtasks = [...subtasks].sort((a, b) => a.order_index - b.order_index)
  const orderedContents = contents
    .map((content, sourceIndex) => ({ content, sourceIndex }))
    .sort((a, b) => a.content.index_ref - b.content.index_ref || a.sourceIndex - b.sourceIndex)
  let subtaskIndex = 0
  let contentIndex = 0

  while (subtaskIndex < orderedSubtasks.length || contentIndex < orderedContents.length) {
    const nextContent = orderedContents[contentIndex]?.content

    if (nextContent && (subtaskIndex >= orderedSubtasks.length || nextContent.index_ref <= orderedItems.length)) {
      orderedItems.push({ type: 'info_box', data: nextContent })
      contentIndex++
      continue
    }

    orderedItems.push({ type: 'subtask', data: orderedSubtasks[subtaskIndex] })
    subtaskIndex++
  }

  return orderedItems
}

export function buildTaskPdfData(allData: Partial<TaskFormData>): TaskPdfData {
  const { title, sub_tasks } = (allData?.SUBTASK_TASK_CREATION || {}) as {
    title: string
    sub_tasks: (Subtask | Content)[]
  }

  const { groups, users, tags } = (allData?.TASK_SETTINGS?.audience || {
    groups: [],
    users: [],
    tags: [],
  }) as TaskAudience

  const {
    is_individual_results,
    send_schedule_push,
    view,
    from_ts: taskSettingsFromTs,
    deadline: taskSettingsDeadline,
    allowed_overdue_days,
    priority,
    disable_gallery_upload,
    rework_limit,
    allow_completion_after_deadline,
  } = (allData?.TASK_SETTINGS || {}) as any

  const {
    from_ts = taskSettingsFromTs,
    deadline = taskSettingsDeadline,
    is_recurring = false,
    days_of_week = [],
    allowed_overdue_days: timeFrameAllowedOverdueDays,
    recurrence_frequency = null,
  } = allData?.TIME_FRAME_SELECTION || {}

  const formattedFromTs = dayjs(from_ts).format(keys.DEFAULT_STRING_FORMAT_TIMESTAMP)
  const formattedDeadline = dayjs(deadline).format(keys.DEFAULT_STRING_FORMAT_TIMESTAMP)

  const { contents, regularSubtasks } = separateTaskContents(sub_tasks || [])

  const subtasksForPdf = regularSubtasks.map((subtask, index) => ({
    ...subtask,
    order_index: index,
    ...(subtask.type === keys.SUBTASK_TYPES.IMAGE_SUBTASK || subtask.type === keys.SUBTASK_TYPES.VIDEO_SUBTASKS
      ? { disable_gallery_upload: disable_gallery_upload || false }
      : {}),
  }))

  const timeFrameFormData = allData[StepType.TIME_FRAME_SELECTION]
  const pdfData: TaskPdfData = {
    title,
    sub_tasks: subtasksForPdf.map(
      (st) =>
        ({
          title: st.title,
          type: st.type,
          is_required: st.is_required,
          order_index: st.order_index,
          category: st.category,
          disable_gallery_upload: st.disable_gallery_upload,
          content: st.content,
        } as TaskPdfSubtask)
    ),
    contents: contents.map(
      (c) =>
        ({
          text: c.text,
          rich_text: c.rich_text,
          images: c.images,
          videos: c.videos,
          files: c.files,
          index_ref: c.index_ref,
        } as TaskPdfContent)
    ),
    audience: { groups: groups || [], users: users || [], tags: tags || [] },
    priority: priority as PRIORITY_VALUES,
    view,
    is_individual_results,
    send_schedule_push,
    disable_gallery_upload,
    rework_limit,
    allow_completion_after_deadline,
    from_ts: formattedFromTs,
    deadline: formattedDeadline,
    is_recurring,
    recurrence_frequency,
    days_of_week,
    frequency_time: timeFrameFormData?.frequency_time,
    monthly_range: timeFrameFormData?.monthly_range,
    allowed_overdue_days: resolveAllowedOverdueDays(timeFrameAllowedOverdueDays, allowed_overdue_days),
  }

  return pdfData
}
